/* How-it-works block — modal open/close, iPhone/Android tabs, per-device
   slider (dots + swipe), a11y. Vanilla JS, no dependencies. Handles multiple
   instances and theme editor re-renders. */

(function () {
  'use strict';

  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  var SWIPE_THRESHOLD_PX = 50;
  var SWIPE_VELOCITY_MS = 300;

  var instances = new WeakMap();

  function HowItWorks(trigger) {
    if (instances.has(trigger)) return instances.get(trigger);

    var modalId = trigger.getAttribute('aria-controls');
    var modal = modalId ? document.getElementById(modalId) : null;
    if (!modal) return null;

    this.trigger = trigger;
    this.modal = modal;
    this.backdrop = modal.querySelector('.how-it-works__backdrop');
    this.closeBtn = modal.querySelector('.how-it-works__close');
    this.tabs = Array.prototype.slice.call(modal.querySelectorAll('.how-it-works__tab'));

    // Build a panel per device with its own slider state
    this.panels = {};
    var self = this;
    var panelNodes = modal.querySelectorAll('.how-it-works__panel');
    Array.prototype.forEach.call(panelNodes, function (panel) {
      var device = panel.getAttribute('data-device');
      if (!device) return;
      self.panels[device] = {
        node: panel,
        viewport: panel.querySelector('.how-it-works__viewport'),
        track: panel.querySelector('.how-it-works__track'),
        slides: Array.prototype.slice.call(panel.querySelectorAll('.how-it-works__slide')),
        dots: Array.prototype.slice.call(panel.querySelectorAll('.how-it-works__dot')),
        index: 0,
        touchStartX: 0,
        touchStartTime: 0,
        touchDeltaX: 0,
        isDragging: false
      };
    });

    // Active device — first tab marked .is-active in Liquid (default: iphone)
    var activeTab = this.tabs.filter(function (t) { return t.classList.contains('is-active'); })[0];
    this.activeDevice = activeTab ? activeTab.getAttribute('data-device') : Object.keys(this.panels)[0];

    this.lastFocused = null;

    this.bind();
    instances.set(trigger, this);
  }

  HowItWorks.prototype.bind = function () {
    var self = this;

    this.trigger.addEventListener('click', function (e) {
      e.preventDefault();
      self.open();
    });

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', function () { self.close(); });
    }

    if (this.backdrop) {
      this.backdrop.addEventListener('click', function () { self.close(); });
    }

    // Tab clicks + keyboard nav
    this.tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () {
        self.activateDevice(tab.getAttribute('data-device'));
      });
      tab.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          var dir = e.key === 'ArrowRight' ? 1 : -1;
          var next = (i + dir + self.tabs.length) % self.tabs.length;
          self.tabs[next].focus();
          self.activateDevice(self.tabs[next].getAttribute('data-device'));
        }
      });
    });

    // Per-panel dot clicks + touch swipe
    Object.keys(this.panels).forEach(function (device) {
      var p = self.panels[device];

      p.dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () { self.goTo(device, i); });
      });

      if (p.viewport) {
        p.viewport.addEventListener('touchstart', function (e) {
          p.touchStartX = e.touches[0].clientX;
          p.touchStartTime = Date.now();
          p.touchDeltaX = 0;
          p.isDragging = true;
          if (p.track) p.track.classList.add('is-dragging');
        }, { passive: true });

        p.viewport.addEventListener('touchmove', function (e) {
          if (!p.isDragging) return;
          p.touchDeltaX = e.touches[0].clientX - p.touchStartX;
          if (p.track) {
            var basePct = -p.index * 100;
            var deltaPct = (p.touchDeltaX / p.viewport.offsetWidth) * 100;
            p.track.style.transform = 'translateX(' + (basePct + deltaPct) + '%)';
          }
        }, { passive: true });

        p.viewport.addEventListener('touchend', function () {
          if (!p.isDragging) return;
          p.isDragging = false;
          if (p.track) p.track.classList.remove('is-dragging');

          var elapsed = Date.now() - p.touchStartTime;
          var absDelta = Math.abs(p.touchDeltaX);
          var isFastSwipe = elapsed < SWIPE_VELOCITY_MS && absDelta > 20;

          if (absDelta > SWIPE_THRESHOLD_PX || isFastSwipe) {
            self.goTo(device, p.index + (p.touchDeltaX < 0 ? 1 : -1));
          } else {
            self.updateTrack(device);
          }
          p.touchDeltaX = 0;
        });
      }
    });

    // Keyboard shortcuts on the modal (Esc, arrows for the active slider)
    this.onKeydown = function (e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        self.close();
      } else if (e.key === 'ArrowRight' && !self.isTabFocused()) {
        e.preventDefault();
        var p = self.panels[self.activeDevice];
        if (p) self.goTo(self.activeDevice, p.index + 1);
      } else if (e.key === 'ArrowLeft' && !self.isTabFocused()) {
        e.preventDefault();
        var p2 = self.panels[self.activeDevice];
        if (p2) self.goTo(self.activeDevice, p2.index - 1);
      } else if (e.key === 'Home' && !self.isTabFocused()) {
        e.preventDefault();
        self.goTo(self.activeDevice, 0);
      } else if (e.key === 'End' && !self.isTabFocused()) {
        e.preventDefault();
        var p3 = self.panels[self.activeDevice];
        if (p3) self.goTo(self.activeDevice, p3.slides.length - 1);
      } else if (e.key === 'Tab') {
        self.trapFocus(e);
      }
    };
  };

  HowItWorks.prototype.isTabFocused = function () {
    return this.tabs.indexOf(document.activeElement) !== -1;
  };

  HowItWorks.prototype.activateDevice = function (device) {
    if (!this.panels[device] || device === this.activeDevice) {
      // Still update tab visuals in case they drifted
      this.updateTabs(device || this.activeDevice);
      return;
    }
    this.activeDevice = device;
    this.updateTabs(device);
    var self = this;
    Object.keys(this.panels).forEach(function (d) {
      var p = self.panels[d];
      var isActive = d === device;
      p.node.classList.toggle('is-active', isActive);
      if (isActive) {
        p.node.removeAttribute('hidden');
      } else {
        p.node.setAttribute('hidden', '');
      }
    });
    // Reset transform without transition so the switch is instant
    var active = this.panels[device];
    if (active && active.track) {
      var prev = active.track.style.transition;
      active.track.style.transition = 'none';
      this.updateTrack(device);
      // Force reflow, then restore transition
      // eslint-disable-next-line no-unused-expressions
      active.track.offsetHeight;
      active.track.style.transition = prev;
    }
  };

  HowItWorks.prototype.updateTabs = function (device) {
    this.tabs.forEach(function (tab) {
      var isActive = tab.getAttribute('data-device') === device;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });
  };

  HowItWorks.prototype.open = function () {
    var self = this;
    this.lastFocused = document.activeElement;
    this.modal.hidden = false;
    // eslint-disable-next-line no-unused-expressions
    this.modal.offsetHeight;
    this.modal.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('how-it-works-open');
    document.addEventListener('keydown', this.onKeydown);
    setTimeout(function () {
      if (self.closeBtn) self.closeBtn.focus();
    }, 50);
  };

  HowItWorks.prototype.close = function () {
    var self = this;
    this.modal.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('how-it-works-open');
    document.removeEventListener('keydown', this.onKeydown);
    var hide = function () { self.modal.hidden = true; };
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      hide();
    } else {
      setTimeout(hide, 200);
    }
    if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
      this.lastFocused.focus();
    }
  };

  HowItWorks.prototype.goTo = function (device, index) {
    var p = this.panels[device];
    if (!p) return;
    var last = p.slides.length - 1;
    if (last < 0) return;
    if (index < 0) index = 0;
    if (index > last) index = last;
    p.index = index;
    this.updateTrack(device);
    this.updateDots(device);
  };

  HowItWorks.prototype.updateTrack = function (device) {
    var p = this.panels[device];
    if (!p || !p.track) return;
    p.track.style.transform = 'translateX(' + (-p.index * 100) + '%)';
  };

  HowItWorks.prototype.updateDots = function (device) {
    var p = this.panels[device];
    if (!p) return;
    var idx = p.index;
    p.dots.forEach(function (dot, i) {
      if (i === idx) {
        dot.classList.add('is-active');
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.classList.remove('is-active');
        dot.removeAttribute('aria-current');
      }
    });
  };

  HowItWorks.prototype.trapFocus = function (e) {
    var focusables = this.modal.querySelectorAll(FOCUSABLE);
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  function initAll(root) {
    var scope = root || document;
    var triggers = scope.querySelectorAll('.how-it-works__trigger');
    triggers.forEach(function (t) { new HowItWorks(t); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
